import type { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ProcessingStepper } from "@/components/ProcessingStepper";
import { cn } from "@/lib/utils";

interface LoadingCardProps {
  message: string;
  description?: string;
  etaLabel?: string;
  status?: string | null;
  children?: ReactNode;
  fullScreen?: boolean;
  showIndicator?: boolean;
}

export const LoadingCard = ({
  message,
  description,
  etaLabel,
  status,
  children,
  fullScreen = false,
  showIndicator = true,
}: LoadingCardProps) => {
  const card = (
    <Card className="w-full max-w-md" data-testid="loading-card">
      <CardContent className="p-8 text-center space-y-4">
        {showIndicator && (status ? (
          <ProcessingStepper status={status} variant="surface" className="px-6" />
        ) : (
          <div
            data-testid="loading-card-spinner"
            className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto"
          />
        ))}
        <div className="space-y-2">
          <p className="text-sm font-medium">{message}</p>
          {etaLabel && <p className="text-xs text-muted-foreground">{etaLabel}</p>}
          {description && (
            <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );

  if (!fullScreen) return card;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {card}
    </div>
  );
};

export const LoadingCardFrame = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("flex items-center justify-center min-h-[60vh] p-4", className)}>
    {children}
  </div>
);
